import { Box, Button, Flex, Text, TextInput } from "@mantine/core"
import { FileIcon, FunnelIcon, InfoIcon, MagnifyingGlassIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { IPullSheetPlus } from "../../models/PullSheet"
import { PullSheetApi } from "../../apis"
import { renderName } from "../../utils/render-name"
import { DeliveryRowAction } from "../delivery/action-controls"
import { DotPill, Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { FilterChip } from "../odoo/filter-chip"
import { ColumnType, Table, TableActions } from "../table"
import { ShippingFilterPanel } from "./shipping-filter-panel"
import { useShippingFilters } from "./shipping-filters"
import { usePullSheetStatus } from "./pull-sheet-status"
import { usePullSheetPdf } from "./use-pull-sheet-pdf"

const STATUS_TONE: Record<string, Tone> = { APPROVED: "grey", LOADING: "amber", CONFIRMED: "blue", LOADED: "green" }

interface Props {
  // «Дууссан» tab: the same list forced to the final status (LOADED = warehouse keeper confirmed),
  // so one list is both the working queue and the archive.
  forcedStatus?: string
}

export function SupplierShippingCustomerList({ forcedStatus }: Props = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation(["delivery", "common"])
  const { shownStateOf } = useDeliveryActions()
  const { nameOf } = usePullSheetStatus()
  const pdf = usePullSheetPdf()
  const tableRef = useRef<any>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const { filters, setFilter, clearFilters, activeFilterChips, searchQuery } = useShippingFilters()
  // The forced status goes in at API-call time only, so the filter panel does not show it as an active chip.
  const apiFilters = forcedStatus ? { ...filters, pullSheetStatus: forcedStatus } : filters

  const reload = () => tableRef.current?.reload()

  const columns: ColumnType<IPullSheetPlus>[] = [
    {
      title: "#",
      width: 40,
      render: (_r, index) => (
        <Text fz={14} fw={600} c="dimmed" ta="center">
          {index + 1}
        </Text>
      ),
    },
    {
      title: t("common:table.action", { ns: "common" }),
      sorter: false,
      render: (record) => {
        const subject = { pullSheetStatus: record.pullSheetStatus }
        return (
          <TableActions>
            <DeliveryRowAction
              label={t("common:actions.detail", { ns: "common" })}
              icon={<InfoIcon size={18} />}
              state={shownStateOf("pullSheetDetail", subject)}
              onClick={() => navigate(`/supplier-shipping/detail/${record.id}`)}
            />
            <DeliveryRowAction
              label={t("delivery:pullSheet.viewPdf")}
              icon={<FileIcon size={18} />}
              state={shownStateOf("pullSheetPdf", subject)}
              loading={pdf.loadingId === record.id}
              onClick={() => pdf.open(record.id as string, { cachedUrl: record.pdf })}
            />
          </TableActions>
        )
      },
    },
    {
      title: t("delivery:pullSheet.refCode"),
      sorter: false,
      dataIndex: "refCode",
      render: (record) => (
        <Text fz={14} fw={700} c="var(--mantine-color-dark-7)" style={{ whiteSpace: "nowrap" }}>
          {record?.refCode ?? "-"}
        </Text>
      ),
    },
    {
      title: t("delivery:pullSheet.route"),
      sorter: false,
      dataIndex: "route",
      render: (record) => {
        const route = record?.route
        if (!route) return <Text fz={14}>-</Text>
        return (
          <Box miw={0}>
            <Text fz={14} fw={700} ff="monospace" truncate>
              {route.routeNumber}
            </Text>
            <Text fz={14} c="gray.8" truncate>
              {route.name ?? route.districtLabel ?? "-"}
            </Text>
          </Box>
        )
      },
    },
    {
      title: t("delivery:pullSheet.loadingDate"),
      sorter: false,
      dataIndex: "loadingDate",
      render: (record) => <DateCell value={record?.loadingDate} />,
    },
    {
      title: t("delivery:pullSheet.loadingStatus"),
      sorter: false,
      dataIndex: "pullSheetStatus",
      render: (record) => <DotPill tone={STATUS_TONE[record?.pullSheetStatus] ?? "grey"}>{nameOf(record?.pullSheetStatus)}</DotPill>,
    },
    {
      title: t("delivery:pullSheet.deliveryDate"),
      sorter: false,
      dataIndex: "deliveryDate",
      render: (record) => <DateCell value={record?.deliveryDate} />,
    },
    {
      title: t("delivery:pullSheet.deliveryStaff"),
      sorter: false,
      dataIndex: "staffFirstNameEmail",
      render: (record) => (
        <Box miw={0} maw={170}>
          <Text fz={14} fw={600} c="var(--mantine-color-dark-7)" truncate>
            {renderName(record?.staff) || "-"}
          </Text>
          <Text fz={14} c="gray.8" truncate>
            {record?.staff?.email ?? "-"}
          </Text>
        </Box>
      ),
    },
    {
      title: t("delivery:pullSheet.orderCount"),
      sorter: false,
      dataIndex: "deliveryNoteCount",
      titleAlign: "right",
      align: "right",
      render: (record) => (
        <Text fz={14} fw={700} ta="right">
          {record?.deliveryNoteCount ?? 0}
        </Text>
      ),
    },
    {
      title: t("delivery:pullSheet.warehouseName"),
      sorter: false,
      dataIndex: "warehouseRefCodeName",
      render: (record) => (
        <Box miw={0}>
          <Text fz={14} c="gray.8" ff="monospace">
            {record?.warehouse?.refCode ?? "-"}
          </Text>
          <Text fz={14} fw={600} c="var(--mantine-color-dark-7)">
            {record?.warehouse?.name ?? "-"}
          </Text>
        </Box>
      ),
    },
  ]

  return (
    <Flex direction="column" gap="md" mih="60vh">
      <Box>
        <Text fz={14} mb={4}>{t("common:actions.search", { ns: "common" })}</Text>
        <Flex gap="sm" align="center" wrap="wrap">
          <TextInput
            w={320}
            value={searchQuery ?? ""}
            onChange={(e) => setFilter("searchQuery", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder={t("delivery:shipping.searchPlaceholder")}
          />
          <Button color="dark" leftSection={<MagnifyingGlassIcon size={16} />} onClick={reload}>
            {t("common:actions.search", { ns: "common" })}
          </Button>
          <Button variant="default" leftSection={<FunnelIcon size={16} />} onClick={() => setFilterOpen(!filterOpen)}>
            {t("common:filter.label", { ns: "common" })}
          </Button>
        </Flex>
        {activeFilterChips.length > 0 && (
          <Flex gap="xs" mt="sm" wrap="wrap">
            {activeFilterChips.map((chip) => (
              <FilterChip {...chip} />
            ))}
          </Flex>
        )}
      </Box>
      <ShippingFilterPanel opened={filterOpen} filters={filters} setFilter={setFilter} clearFilters={clearFilters} onSearch={reload} />
      <Table name="supplier-shipping-customer.list" horizontalSpacing="sm" filters={apiFilters} columns={columns} loadData={(data) => PullSheetApi.list(data!)} ref={tableRef} />
    </Flex>
  )
}

function DateCell({ value }: Readonly<{ value?: string | null }>) {
  if (!value) return <Text fz={14}>-</Text>
  return (
    <Text fz={14} ff="monospace">
      {dayjs(value).format("YYYY-MM-DD HH:mm")}
    </Text>
  )
}
